import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Send, Loader2, Zap, Truck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, SERVICE_AREAS, AREAS, waLink, scrollToId } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/messages`, form);
      toast.success("Message sent — we'll get back to you fast.");
      setForm({ name: "", contact: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-obsidian border border-white/15 rounded-sm px-4 py-3 text-sm text-white placeholder:text-mist/50 focus:outline-none focus:border-ember transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-charcoal border-t border-white/10 pt-16 sm:pt-24 lg:pt-32 noise-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <Eyebrow>Get In Touch</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
              Your driveway.<br /><span className="text-ember">Our shop bay.</span>
            </h2>

            <div className="mt-10 space-y-4">
              <a data-testid="contact-phone-link" href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-4 group">
                <span className="w-11 h-11 bg-panel border border-white/10 rounded-sm flex items-center justify-center group-hover:border-ember/50 transition-colors"><Phone className="w-5 h-5 text-ember" /></span>
                <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Call or Text</p><p className="text-white font-semibold group-hover:text-ember transition-colors">{SITE.phoneDisplay}</p></div>
              </a>
              <a data-testid="contact-whatsapp-link" href={waLink("Hi GoGoAuto! I have a question about my vehicle.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                <span className="w-11 h-11 bg-panel border border-white/10 rounded-sm flex items-center justify-center group-hover:border-[#25D366]/60 transition-colors"><MessageCircle className="w-5 h-5 text-[#25D366]" /></span>
                <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">WhatsApp</p><p className="text-white font-semibold group-hover:text-[#25D366] transition-colors">{SITE.phoneDisplay}</p></div>
              </a>
              <a data-testid="contact-email-link" href={`mailto:${SITE.email}`} className="flex items-center gap-4 group">
                <span className="w-11 h-11 bg-panel border border-white/10 rounded-sm flex items-center justify-center group-hover:border-ember/50 transition-colors"><Mail className="w-5 h-5 text-ember" /></span>
                <div><p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist">Email</p><p className="text-white font-semibold group-hover:text-ember transition-colors">{SITE.email}</p></div>
              </a>
            </div>

            <div className="mt-10">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-ember" /> Service Area</p>
              <div className="flex flex-wrap gap-2" data-testid="service-area-badges">
                {SERVICE_AREAS.map((a) => (
                  <span key={a} className="font-display font-bold uppercase tracking-wider text-sm px-4 py-1.5 border border-white/15 rounded-sm text-mist hover:text-ember hover:border-ember/50 transition-colors cursor-default">
                    {a}
                  </span>
                ))}
              </div>
              <p className="mt-5 flex items-start gap-2 text-sm text-mist max-w-md">
                <Truck className="w-4 h-4 text-ember mt-0.5 shrink-0" />
                Prefer off-site? Your vehicle can be serviced at our location — vehicle pick-up is also available.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="bg-panel border border-white/10 rounded-sm p-7 sm:p-9" data-testid="contact-form">
              <h3 className="font-display font-bold uppercase text-2xl tracking-tight">Quick Message</h3>
              <p className="text-mist text-sm mt-1 mb-7">Questions or a fast quote — we reply quickly.</p>
              <div className="space-y-5">
                <div>
                  <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Name</label>
                  <input data-testid="contact-name-input" type="text" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Phone or Email</label>
                  <input data-testid="contact-reply-input" type="text" value={form.contact} onChange={set("contact")} placeholder="How should we reach you?" className={inputCls} />
                </div>
                <div>
                  <label className="font-mono2 text-xs tracking-[0.25em] uppercase text-mist block mb-2">Message</label>
                  <textarea data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your vehicle or issue…" className={inputCls} />
                </div>
                <button
                  data-testid="contact-send-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 bg-ember text-obsidian font-display font-bold uppercase tracking-wider text-lg px-7 py-4 rounded-sm hover:bg-[#9BE873] hover:shadow-[0_0_28px_rgba(132,220,90,0.35)] disabled:opacity-50 transition-all duration-200"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>

      <footer className="mt-20 border-t border-white/10 bg-obsidian" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mist mb-4">Mobile Mechanic Near You</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="footer-area-links">
            {Object.entries(AREAS).map(([slug, a]) => (
              <a
                key={slug}
                href={`/areas/${slug}`}
                data-testid={`footer-area-${slug}`}
                className="font-display font-bold uppercase tracking-wider text-sm text-mist hover:text-ember transition-colors"
              >
                {a.name}
              </a>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <button data-testid="footer-logo" onClick={() => scrollToId("#home")} className="flex items-center gap-2">
            <img src="/logo.jpg" alt="GoGo Auto Repair Services" className="h-12 w-auto rounded-sm" />
          </button>
          <p className="text-mist text-xs text-center sm:text-right leading-relaxed">
            Licensed 310S Automotive Service Technician · {SITE.area}<br />
            © {new Date().getFullYear()} GoGoAuto Mobile Repair &amp; Service. All rights reserved.
          </p>
        </div>
      </footer>
    </section>
  );
};
